import { NextPageParams, NotificationsResponse, PaginationMetadata } from "@/schema"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import { fetchNotifications, fetchUnreadNotificationCount } from "./api"

export function useInfiniteNotifications() {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useInfiniteQuery<
    NotificationsResponse,
    Error,
    { notifications: NotificationsResponse["notifications"]; pagination: PaginationMetadata },
    string[],
    NextPageParams | undefined
  >({
    queryKey: ["notifications", bookId, "infinite"],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchNotifications(context, pageParam)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      notifications: data.pages.flatMap((p) => p.notifications),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useUnreadNotificationsCount() {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery({
    queryKey: ["notifications", bookId, "unread_count"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const res = await fetchUnreadNotificationCount(context)
      return res.unread_count
    },
    refetchInterval: 60_000,
  })
}
